// UserSettingsStore
import { defineStore } from 'pinia'
import { ref } from 'vue';
import { UserSettingGroup } from '../../Enums/ChoiceEnums';
import * as ChoiceApi from '../ChoiceApi'

export const useUserSettingsStore = defineStore('UserSettingsStore', () => {

    const userSettings = ref<Record<string, any>>({});

    // Get a user setting.  If we don't have it, get it from the API, otherwise use the local copy
    async function getUserSettingString(group: UserSettingGroup, codeName: string, updateStoreValue: boolean = true): Promise<string | undefined>
    {
        const currentValue = userSettings.value[SettingKeyString(group, codeName)];
        if (undefined !== currentValue)
        {
            if ("string" !== typeof(currentValue))
            {
                alert(`Problem getting user setting: ${group}/${codeName} -- expected a string value.`);
                return;
            }
            return currentValue.toString();
        }

        // Fetch the value
        const fetchedValue = await ChoiceApi.user.getSetting(group, codeName);

        if (updateStoreValue)
            updateStore(group, codeName, fetchedValue);
        return fetchedValue;
    }

    // Get a user settings that is stored as JSON
    async function getUserSettingObj<TObj>(group: UserSettingGroup, codeName: string): Promise<TObj | undefined>
    {
        const setting = await getUserSettingString(group, codeName, false);
        if (undefined === setting || "" === setting)
            return;
        const settingObject = JSON.parse(setting) as TObj;
        updateStore(group, codeName, settingObject);
        return settingObject;
    }

    function updateStore(group: UserSettingGroup, codeName: string, value: unknown)
    {
        userSettings.value[SettingKeyString(group, codeName)] = value;
    }


    // Save a user setting from the API
    async function saveUserSetting(group: UserSettingGroup, codeName: string, value?: unknown, errorCallback?: (httpResp: Response) => void): Promise<void>
    {
        const keyString = SettingKeyString(group, codeName);
        
        // Save the value
        if (!await ChoiceApi.user.saveSetting(group, codeName, value))
        {
            if (!errorCallback)
                alert(`Error saving user setting: ${group}/${codeName}`);
        }
        userSettings.value[keyString] = value;
    }
    
    // Use when we want to reset the user setting
    function clearUserSetting(group: UserSettingGroup, codeName: string)
    {
        delete userSettings.value[SettingKeyString(group, codeName)];
    }    

    // Generate a string for the setting
    function SettingKeyString(group: UserSettingGroup, codeName: string): string
    {
        return `${group}|${codeName}`;
    }

    async function getEmrTimelineSettingString(settingName: EmrTimelineSetting, updateStoreValue: boolean = true): Promise<string | undefined>
    {
        return await getUserSettingString(UserSettingGroup.EMRTimelineSettings, settingName, updateStoreValue);
    }

    // Get all the user parameter group settings in bulk and cache in the store
    async function loadGroupSettings(group: UserSettingGroup): Promise<void>
    {
        const values = await ChoiceApi.user.getSettingGroupValues(group);
        if (values)
        {
            for(const value of values)
            {
                updateStore(group, value.parmCodeName, value.value);
            }
        }
    }

    return { userSettings, getUserSettingString, getUserSettingObj, saveUserSetting, clearUserSetting, loadGroupSettings, getEmrTimelineSettingString };
});

export enum EmrTimelineSetting
{
    PriorAuthEnabled = "PAE",
    TelemedicineEnabled = "TME",
    PatientReportedEnabled = "PRE",
    DocumentsEnabled = "DOE",
    OrderResultsEnabled = "LRE",
    ObstetricsEnabled = "OBE",
    ImmunizationsEnabled = "IME",
    MedicationsEnabled = "MDE",
    MailEnabled = "CME",
    ProgressNotesEnabled = "SPE",   // Progress/Soap Notes
    VitalsEnabled = "VTE",
}